'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { withPosterFragment } from '@kit/shared/video-poster';
import { Trans } from '@kit/ui/trans';

import { FadeIn, SlideIn } from './animations';

const FONT_BEBAS = 'var(--font-bebas-neue), Impact, sans-serif';
const FONT_INTER = 'var(--font-inter-agency), -apple-system, sans-serif';

const photos = [
  '/agency/photos/p1.jpg',
  '/agency/photos/p2.jpg',
  '/agency/photos/p3.jpg',
  '/agency/photos/p4.jpg',
  '/agency/photos/p5.jpg',
  '/agency/photos/p6.jpg',
  '/agency/photos/p7.jpg',
  '/agency/photos/p8.jpg',
  '/agency/photos/p9.jpg',
  '/agency/photos/p10.jpg',
  '/agency/photos/p11.jpg',
  '/agency/photos/p12.jpg',
  '/agency/photos/p13.jpg',
  '/agency/photos/p14.jpg',
  '/agency/photos/p15.jpg',
];

const worlds = [
  {
    titleKey: 'agency:worlds.weddings',
    video: '/agency/videos/wedding-0.mp4',
    testId: 'featured-wedding-video',
  },
  {
    titleKey: 'agency:worlds.brand',
    video: '/agency/videos/wedding-1.mp4',
    testId: 'featured-brand-video',
  },
  {
    titleKey: 'agency:worlds.photoshoots',
    video: '/agency/videos/fashion-0.mp4',
    testId: 'featured-fashion-video',
  },
  {
    titleKey: 'agency:worlds.creative',
    video: '/agency/videos/fashion-2.mp4',
    testId: 'featured-creative-video',
  },
];

export function DancerPhotosSection() {
  const { t } = useTranslation('agency');
  const marqueeStyle: CSSProperties = {
    width: 'max-content',
    animation: 'agency-marquee 60s linear infinite',
    willChange: 'transform',
  };

  return (
    <section
      className="border-border overflow-hidden border-t"
      data-testid="dancer-photos-section"
    >
      <div
        className="flex"
        style={marqueeStyle}
        onMouseEnter={(e) =>
          (e.currentTarget.style.animationPlayState = 'paused')
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.animationPlayState = 'running')
        }
      >
        {[...photos, ...photos].map((src, i) => (
          <div
            key={i}
            className="relative aspect-[4/5] w-[45vw] flex-shrink-0 overflow-hidden bg-black sm:w-[32vw] md:w-[clamp(260px,22vw,340px)]"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt=""
              className="h-full w-full object-cover object-bottom grayscale-[15%] transition-all duration-700 hover:scale-105 hover:grayscale-0"
            />
          </div>
        ))}
      </div>

      <div className="mx-auto max-w-7xl px-6 pt-20 pb-4 sm:px-8 sm:pt-24 sm:pb-6 md:px-16 md:pt-32 md:pb-8">
        <FadeIn>
          <h2
            className="text-foreground mx-auto max-w-4xl text-center leading-[1.1]"
            style={{
              fontFamily: FONT_BEBAS,
              fontWeight: 300,
              fontSize: 'clamp(1.9rem, 3.6vw, 3.2rem)',
              letterSpacing: '0.02em',
            }}
          >
            <Trans
              i18nKey="agency:worlds.headline"
              components={{
                em: (
                  <em
                    style={{
                      fontFamily: FONT_INTER,
                      fontStyle: 'italic',
                      fontWeight: 300,
                    }}
                  />
                ),
              }}
            />
          </h2>
          <p
            className="text-muted-foreground mx-auto mt-5 mb-12 max-w-xl text-center leading-relaxed md:mt-6 md:mb-16"
            style={{
              fontFamily: FONT_INTER,
              fontWeight: 300,
              fontSize: 'clamp(0.9rem, 1.1vw, 1rem)',
              letterSpacing: '0.02em',
              lineHeight: 1.8,
            }}
          >
            <Trans i18nKey="agency:worlds.subtitle" />
          </p>
        </FadeIn>

        <div className="grid gap-x-10 gap-y-20 md:grid-cols-2 md:gap-y-28">
          {worlds.map((w, i) => (
            <SlideIn key={w.titleKey} delay={0.06 * i}>
              <h3
                className="text-foreground mb-6 flex items-end justify-center text-center leading-[1.15] md:mb-8 md:min-h-[2.6em]"
                style={{
                  fontFamily: FONT_BEBAS,
                  fontWeight: 300,
                  fontSize: 'clamp(1.5rem, 2.6vw, 2.2rem)',
                  letterSpacing: '0.02em',
                }}
                data-testid={`world-title-${i}`}
              >
                {t(w.titleKey)}
              </h3>
              <div
                className="relative w-full overflow-hidden bg-black"
                style={{ aspectRatio: '16 / 9' }}
              >
                <video
                  src={withPosterFragment(w.video)}
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover"
                  data-testid={w.testId}
                />
              </div>
              <div className="mt-5 text-center">
                <a
                  href="/video-gallery"
                  className="text-muted-foreground hover:text-foreground border-muted-foreground/40 hover:border-foreground inline-block border-b pb-1 text-[0.65rem] tracking-[0.25em] uppercase transition-colors duration-300 sm:text-[0.7rem]"
                  style={{ fontFamily: FONT_INTER }}
                >
                  <Trans i18nKey="agency:worlds.seeMore" />{' '}
                  <span aria-hidden>→</span>
                </a>
              </div>
            </SlideIn>
          ))}
        </div>

        <FadeIn delay={0.15}>
          <div className="mt-12 flex justify-center md:mt-16">
            <a
              href="/video-gallery"
              className="border-foreground/40 text-foreground hover:bg-foreground hover:text-background active:bg-foreground active:text-background inline-block border px-6 py-3 text-center text-[0.65rem] tracking-[0.25em] whitespace-nowrap uppercase transition-all duration-500 sm:px-8 sm:py-4 sm:text-[0.7rem] sm:tracking-[0.3em]"
              style={{ fontFamily: FONT_INTER }}
              data-testid="cta-video-gallery"
            >
              <Trans i18nKey="agency:worlds.seeAll" />
            </a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
